<template>
  <div v-if="visible" class="lp-tag-manager">
    <CustomModal @onClose="close">
      <div
        class="lp-tag-manager-content"
        role="dialog"
        aria-modal="true"
        aria-label="Tags verwalten"
      >
        <div class="lp-tag-manager-header">
          <div class="lp-tag-manager-title-wrap">
            <span class="lp-tag-manager-title-icon"
              ><i class="fa fa-tag"></i
            ></span>
            <div>
              <h3>Tags verwalten</h3>
              <p>
                Fügen Sie Tags hinzu, bearbeiten oder entfernen Sie bestehende
                Tags.
              </p>
            </div>
          </div>
        </div>

        <div class="lp-tag-manager-body">
          <div class="lp-tag-manager-editor">
            <div class="lp-tag-manager-editor-title">Tag-Editor</div>
            <div v-if="!canEdit" class="lp-tag-manager-readonly">
              Sie haben nur Leserechte.
            </div>

            <div class="lp-tag-manager-input-row">
              <input
                ref="tagNameInputRef"
                v-model.trim="editor.name"
                type="text"
                maxlength="60"
                class="lp-tag-manager-input"
                placeholder="Tag eingeben..."
                :disabled="!canEdit"
                @keydown.enter.prevent="saveTag"
                @input="clearValidationError"
              />
              <button
                type="button"
                class="button save lp-tag-manager-save-btn"
                :disabled="!canEdit"
                @click="saveTag"
              >
                {{ isEditMode ? 'Aktualisieren' : 'Hinzufügen' }}
              </button>
              <button
                v-if="isEditMode"
                type="button"
                class="button lp-tag-manager-cancel-btn"
                @click="resetEditor"
              >
                Abbrechen
              </button>
            </div>
            <div v-if="validationError" class="lp-tag-manager-error">
              {{ validationError }}
            </div>

            <div class="lp-tag-manager-color-grid">
              <button
                v-for="color in tagColorsList"
                :key="`lp-tag-color-${color}`"
                type="button"
                class="lp-tag-color-item"
                :class="{ selected: editor.color === color }"
                :style="{ 'background-color': color }"
                :disabled="!canEdit"
                @click="editor.color = color"
              ></button>
            </div>

            <div
              v-if="suggestedTags.length"
              class="lp-tag-manager-suggestion-wrap"
            >
              <div class="lp-tag-manager-suggestion-title">Vorschläge</div>
              <div class="lp-tag-suggestion-list">
                <button
                  v-for="suggestion in suggestedTags"
                  :key="`lp-tag-suggestion-${suggestion.id}-${suggestion.name}`"
                  type="button"
                  class="lp-tag-suggestion-item"
                  :disabled="!canEdit"
                  @click="addSuggestedTag(suggestion)"
                >
                  <span
                    class="badge badge-secondary lp-tag-suggestion-badge"
                    :style="{ 'background-color': suggestion.color }"
                  >
                    {{ suggestion.name }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="lp-tag-manager-list-wrap">
            <div class="lp-tag-manager-list-title">Bestehende Tags</div>

            <div class="lp-tag-list-empty" v-if="!localTags.length">
              Keine Tags vorhanden.
            </div>

            <div
              v-for="(tag, index) in localTags"
              :key="`lp-tag-${tag.id || tag.name}-${index}`"
              class="lp-tag-item"
            >
              <span
                class="badge badge-secondary lp-tag-label"
                :style="{ 'background-color': tag.color || '#4f46e5' }"
              >
                {{ tag.name }}
              </span>
              <div class="lp-tag-actions" v-if="canEdit">
                <button
                  type="button"
                  class="lp-tag-action-btn"
                  title="Bearbeiten"
                  @click="startEdit(tag, index)"
                >
                  <i class="fa fa-pen"></i>
                </button>
                <button
                  type="button"
                  class="lp-tag-action-btn"
                  title="Entfernen"
                  @click="removeTag(index)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from '@/components/modals/CustomModal.vue';

export default {
  name: 'LoadProfileTagManagerModal',
  components: {
    CustomModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    tags: {
      type: Array,
      default: () => [],
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localTags: [],
      tagColorsList: [
        '#4f46e5',
        '#0ea5e9',
        '#06b6d4',
        '#22c55e',
        '#84cc16',
        '#eab308',
        '#f97316',
        '#ef4444',
        '#ec4899',
        '#8b5cf6',
      ],
      editor: {
        index: null,
        name: '',
        color: '#4f46e5',
        id: null,
      },
      validationError: '',
      hasLocalMutations: false,
    };
  },
  computed: {
    isEditMode() {
      return Number.isInteger(this.editor.index);
    },
    normalizedSuggestions() {
      return (this.suggestions || [])
        .map((tag) => this.normalizeIncomingTag(tag))
        .filter((tag) => !!tag.name);
    },
    suggestedTags() {
      const existingNames = new Set(
        this.localTags
          .map((tag) => this.normalizeTagName(tag?.name).toLowerCase())
          .filter((name) => !!name)
      );
      const existingIds = new Set(
        this.localTags
          .map((tag) => tag?.id)
          .filter((id) => id !== null && id !== undefined && `${id}`.trim())
          .map((id) => String(id))
      );
      const searchTerm = this.normalizeTagName(this.editor.name).toLowerCase();

      return this.normalizedSuggestions
        .filter((tag) => {
          if (tag?.id === null || tag?.id === undefined) return true;
          return !existingIds.has(String(tag.id));
        })
        .filter((tag) => !existingNames.has(tag.name.toLowerCase()))
        .filter((tag) => {
          if (!searchTerm) return true;
          return tag.name.toLowerCase().includes(searchTerm);
        })
        .slice(0, 18);
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.initLocalState();
      }
    },
    tags: {
      deep: true,
      handler(newTags) {
        if (this.visible) {
          if (this.hasLocalMutations) return;
          this.localTags = this.normalizeTags(newTags || []);
        }
      },
    },
  },
  methods: {
    initLocalState() {
      this.hasLocalMutations = false;
      this.localTags = this.normalizeTags(this.tags || []);
      this.resetEditor();
      this.$nextTick(() => {
        if (this.$refs.tagNameInputRef) {
          this.$refs.tagNameInputRef.focus();
        }
      });
    },
    resetEditor() {
      this.editor = {
        index: null,
        name: '',
        color: this.tagColorsList[0],
        id: null,
      };
      this.validationError = '';
    },
    close() {
      this.hasLocalMutations = false;
      this.$emit('close');
    },
    normalizeTagName(tagName) {
      if (typeof tagName !== 'string') return '';
      return tagName.trim();
    },
    normalizeIncomingTag(tag) {
      if (tag === null || tag === undefined) return null;

      if (typeof tag === 'object' && !Array.isArray(tag)) {
        const name = this.normalizeTagName(tag?.name || tag?.n);
        if (!name) return null;
        return {
          id: tag?.id ?? null,
          name,
          color: tag?.color || tag?.c || this.tagColorsList[0],
        };
      }

      const suggestionById = (this.suggestions || []).find(
        (suggestion) =>
          suggestion &&
          typeof suggestion === 'object' &&
          !Array.isArray(suggestion) &&
          String(suggestion.id) === String(tag)
      );
      if (suggestionById) {
        const resolvedName = this.normalizeTagName(
          suggestionById.name || suggestionById.n || ''
        );
        if (!resolvedName) return null;
        return {
          id: suggestionById.id ?? tag,
          name: resolvedName,
          color:
            suggestionById.color || suggestionById.c || this.tagColorsList[0],
        };
      }
      return null;
    },
    normalizeTags(tags = []) {
      return (tags || [])
        .map((tag) => this.normalizeIncomingTag(tag))
        .filter((tag) => !!tag);
    },
    emitChange(previousTags, nextTags) {
      this.$emit('change', {
        previousTags: JSON.parse(JSON.stringify(previousTags || [])),
        nextTags: JSON.parse(JSON.stringify(nextTags || [])),
      });
    },
    clearValidationError() {
      if (this.validationError) this.validationError = '';
    },
    getDuplicateTagIndex(formattedName) {
      const normalizedName = this.normalizeTagName(formattedName).toLowerCase();
      return this.localTags.findIndex((tag, index) => {
        if (index === this.editor.index) return false;
        return (
          this.normalizeTagName(tag?.name).toLowerCase() === normalizedName
        );
      });
    },
    saveTag() {
      if (!this.canEdit) return;
      this.clearValidationError();

      const formattedName = this.normalizeTagName(this.editor.name);
      if (!formattedName) {
        this.validationError = 'Bitte geben Sie einen Tag-Namen ein.';
        return;
      }

      const duplicateIndex = this.getDuplicateTagIndex(formattedName);
      if (duplicateIndex !== -1) {
        this.validationError =
          'Ein Tag mit diesem Namen existiert bereits.';
        return;
      }

      const previousTags = JSON.parse(JSON.stringify(this.localTags));
      const payload = {
        name: formattedName,
        color: this.editor.color || this.tagColorsList[0],
        id: this.editor.id || null,
      };

      if (this.isEditMode) {
        this.$set(this.localTags, this.editor.index, payload);
      } else {
        this.localTags.push(payload);
      }

      this.hasLocalMutations = true;
      this.emitChange(previousTags, this.localTags);
      this.resetEditor();
    },
    addSuggestedTag(suggestion) {
      if (!this.canEdit) return;
      this.clearValidationError();
      this.editor = {
        index: null,
        name: suggestion?.name || '',
        color: suggestion?.color || this.tagColorsList[0],
        id: suggestion?.id || null,
      };
      this.saveTag();
    },
    startEdit(tag, index) {
      if (!this.canEdit) return;
      this.clearValidationError();
      this.editor = {
        index,
        name: tag?.name || '',
        color: tag?.color || this.tagColorsList[0],
        id: tag?.id || null,
      };
      this.$nextTick(() => {
        if (this.$refs.tagNameInputRef) {
          this.$refs.tagNameInputRef.focus();
        }
      });
    },
    removeTag(index) {
      if (!this.canEdit) return;
      if (index < 0 || index >= this.localTags.length) return;

      const previousTags = JSON.parse(JSON.stringify(this.localTags));
      this.localTags.splice(index, 1);
      this.hasLocalMutations = true;
      this.emitChange(previousTags, this.localTags);

      if (this.editor.index === index) {
        this.resetEditor();
      } else if (
        Number.isInteger(this.editor.index) &&
        this.editor.index > index
      ) {
        this.editor.index -= 1;
      }
    },
  },
};
</script>

<style scoped>
.lp-tag-manager {
  position: relative;
  text-align: left;
}

.lp-tag-manager ::v-deep .custom-modal-wrapper .custom-modal {
  width: min(640px, 96vw);
  max-height: 60vh;
  border: 0;
  border-radius: 14px;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.lp-tag-manager ::v-deep .custom-modal-wrapper .custom-modal-backdrop {
  background: rgba(0, 0, 0, 0.45);
}

.lp-tag-manager ::v-deep .custom-modal-wrapper .custom-modal-close-icon {
  top: 14px;
  right: 14px;
  color: #111;
}

.lp-tag-manager-content {
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.lp-tag-manager-header {
  border-bottom: 1px solid #e5e5e5;
  padding: 14px 16px;
}

.lp-tag-manager-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-right: 34px;
}

.lp-tag-manager-title-icon {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #111;
  margin-top: 1px;
}

.lp-tag-manager-header h3 {
  margin: 0;
  font-size: 18px;
  color: #111;
}

.lp-tag-manager-header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #4b4b4b;
}

.lp-tag-manager-body {
  flex: 1;
  min-height: 0;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.lp-tag-manager-editor,
.lp-tag-manager-list-wrap {
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 12px;
}

.lp-tag-manager-list-wrap {
  flex: 1;
  min-height: 0;
  margin-top: 12px;
  overflow-y: auto;
}

.lp-tag-manager-editor-title,
.lp-tag-manager-list-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #111;
  margin-bottom: 10px;
}

.lp-tag-manager-readonly {
  font-size: 12px;
  color: #4b4b4b;
  margin-bottom: 10px;
}

.lp-tag-manager-error {
  color: #b42318;
  font-size: 12px;
  margin-top: 6px;
}

.lp-tag-manager-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lp-tag-manager-input {
  flex: 1;
  min-height: 36px;
  border: 1px solid #111;
  border-radius: 10px;
  background: #fff;
  color: #111;
  font-size: 14px;
  padding: 0 12px;
}

.lp-tag-manager-color-grid {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.lp-tag-manager-suggestion-wrap {
  margin-top: 12px;
}

.lp-tag-manager-suggestion-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4b4b4b;
  margin-bottom: 8px;
}

.lp-tag-suggestion-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.lp-tag-suggestion-item {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.lp-tag-suggestion-item:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.lp-tag-suggestion-badge {
  padding: 4px 8px;
}

.lp-tag-color-item {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
}

.lp-tag-color-item.selected {
  box-shadow: 0 0 0 1px #111;
}

.lp-tag-manager-save-btn {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid #111;
  background: #111;
  color: #fff;
  font-weight: 600;
  padding: 0 14px;
}

.lp-tag-manager-save-btn:hover {
  background: #000;
  border-color: #000;
}

.lp-tag-manager-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.lp-tag-manager-cancel-btn {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid #111;
  background: #fff;
  color: #111;
  font-weight: 600;
  padding: 0 14px;
}

.lp-tag-manager-cancel-btn:hover {
  background: #f4f4f4;
}

.lp-tag-list-empty {
  color: #4b4b4b;
  font-size: 13px;
  border: 1px dashed #111;
  border-radius: 10px;
  padding: 12px;
}

.lp-tag-item {
  min-height: 30px;
  padding: 2px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.lp-tag-item + .lp-tag-item {
  margin-top: 2px;
}

.lp-tag-label {
  max-width: 80%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.lp-tag-actions {
  display: flex;
  gap: 6px;
}

.lp-tag-action-btn {
  border: 0;
  background: transparent;
  color: #111;
  width: auto;
  height: auto;
  padding: 2px 4px;
  border-radius: 0;
  cursor: pointer;
}

.lp-tag-action-btn:hover {
  background: transparent;
  color: #000;
}

@media (max-width: 768px) {
  .lp-tag-manager-input-row {
    flex-wrap: wrap;
  }

  .lp-tag-manager-input-row .button {
    min-width: 140px;
  }

  .lp-tag-label {
    max-width: 70%;
  }
}
</style>
